/**
 * Hook useConversationPhaseAutomations - Detecta automações vinculadas a fases do Kanban de Conversas
 *
 * RELEASE: 2.0.0 - 2026-05-09
 *   - Migrado para arquitetura 100% server-side. Substitui os 3 SELECTs diretos
 *     anteriores (kanban_phases, flows, routing_rules) por uma única chamada à
 *     RPC `get_conversation_phase_automations(p_category_id, p_tenant_hint)`,
 *     que retorna o PhaseAutomationsMap pronto para consumo.
 *   - Mantém helpers (hasAutomation, automationCount, getTriggerAutomations,
 *     getActionAutomations, totalAutomations), tipos e queryKey originais
 *     para preservar invalidações já configuradas pelo restante do app.
 */

import { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { logger } from '@/lib/logger';
import type { PhaseAutomation, PhaseAutomationsMap } from '@/hooks/usePhaseAutomations';

// Re-exporta os tipos para consistência
export type { PhaseAutomation, PhaseAutomationsMap };

/**
 * Hook para buscar automações vinculadas a fases do Kanban de Conversas
 *
 * @param categoryId - ID da categoria do kanban (opcional)
 * @returns Mapa de automações por fase e funções utilitárias
 */
export function useConversationPhaseAutomations(categoryId: string | null) {
  const { currentTenant } = useAuth();

  // =========================================================================
  // Query — agora delega TUDO para a RPC server-side
  // =========================================================================
  const { data: automationsByPhase = {}, isLoading, refetch } = useQuery({
    queryKey: ['conversation-phase-automations', categoryId, currentTenant?.id],
    queryFn: async (): Promise<PhaseAutomationsMap> => {
      if (!categoryId || !currentTenant?.id) return {};

      logger.info(
        '[useConversationPhaseAutomations] Buscando automações via RPC para categoria:',
        categoryId,
      );

      const { data, error } = await (supabase.rpc as any)(
        'get_conversation_phase_automations',
        {
          p_category_id: categoryId,
          p_tenant_hint: currentTenant.id,
        },
      );

      if (error) {
        logger.error(
          '[useConversationPhaseAutomations] Erro na RPC get_conversation_phase_automations:',
          error.message,
        );
        return {};
      }

      // A RPC retorna JSONB já no shape PhaseAutomationsMap.
      const map = (data || {}) as PhaseAutomationsMap;

      logger.info(
        '[useConversationPhaseAutomations] Automações encontradas:',
        Object.entries(map).map(([k, v]) => `${k}: ${v.length}`).join(', '),
      );

      return map;
    },
    enabled: !!categoryId && !!currentTenant?.id,
    staleTime: 60 * 1000, // 1 minuto
  });

  // =========================================================================
  // Funções utilitárias (memoizadas) — assinatura inalterada
  // =========================================================================

  /** Verifica se uma fase tem automações configuradas */
  const hasAutomation = useCallback((phaseId: string): boolean => {
    return (automationsByPhase[phaseId]?.length || 0) > 0;
  }, [automationsByPhase]);

  /** Retorna a contagem de automações de uma fase */
  const automationCount = useCallback((phaseId: string): number => {
    return automationsByPhase[phaseId]?.length || 0;
  }, [automationsByPhase]);

  /** Retorna automações do tipo GATILHO (disparam quando conversa entra na fase) */
  const getTriggerAutomations = useCallback((phaseId: string): PhaseAutomation[] => {
    return (automationsByPhase[phaseId] || []).filter(a => a.category === 'trigger');
  }, [automationsByPhase]);

  /** Retorna automações do tipo AÇÃO (movem conversas PARA esta fase) */
  const getActionAutomations = useCallback((phaseId: string): PhaseAutomation[] => {
    return (automationsByPhase[phaseId] || []).filter(a => a.category === 'action');
  }, [automationsByPhase]);

  /** Retorna o total de automações em todas as fases */
  const totalAutomations = useMemo(() => {
    return Object.values(automationsByPhase).reduce(
      (sum, automations) => sum + automations.length,
      0,
    );
  }, [automationsByPhase]);

  return {
    automationsByPhase,
    isLoading,
    refetch,
    // Helpers
    hasAutomation,
    automationCount,
    getTriggerAutomations,
    getActionAutomations,
    totalAutomations,
  };
}

